import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, type Locale } from '../../hooks/useT';

function CGUBodyFr() {
  return (
    <>
      <h2>1. Objet</h2>
      <p>
        Les présentes CGU régissent l&apos;accès et l&apos;utilisation du jeu en ligne{' '}
        <strong>Dezoom</strong>, édité par PrograGames (Florian Augeard). En accédant au jeu,
        tu acceptes ces conditions.
      </p>
      <h2>2. Éditeur</h2>
      <p>
        PrograGames — Florian Augeard, 49000 Angers, France. Contact :{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
        .
      </p>
      <h2>3. Accès au jeu</h2>
      <p>
        Dezoom est un jeu de devinettes photo dans le navigateur. Le défi du jour est gratuit.
        Le mode infini, l&apos;arène classée et certaines fonctionnalités avancées nécessitent
        l&apos;achat du mode premium (voir <Link to="/cgv">CGV</Link>). Une connexion Internet est requise.
      </p>
      <h2>4. Âge et capacité</h2>
      <p>
        Le jeu s&apos;adresse aux personnes de <strong>16 ans et plus</strong>. Si tu es mineur,
        l&apos;utilisation doit se faire avec l&apos;autorisation d&apos;un parent ou titulaire de l&apos;autorité parentale.
      </p>
      <h2>5. Compte joueur</h2>
      <ul>
        <li>Tu peux jouer en <strong>invité</strong> ou créer un <strong>compte</strong> (email, pseudo).</li>
        <li>Tu es responsable de la confidentialité de ton accès (lien magique, session).</li>
        <li>Le pseudo doit être unique et ne pas porter atteinte aux droits de tiers.</li>
        <li>La progression (série, scores, Elo, ligues) est liée au compte connecté.</li>
      </ul>
      <h2>6. Fonctionnalités</h2>
      <ul>
        <li><strong>Défi du jour</strong> : une énigme officielle par jour, classement quotidien.</li>
        <li><strong>Mode infini</strong> (premium) : énigmes illimitées depuis la banque premium.</li>
        <li><strong>Arène</strong> (premium) : matchs classés best-of-5 avec classement Elo.</li>
        <li><strong>Amis</strong> : ajout par pseudo, défis entre amis premium.</li>
        <li><strong>Ligues</strong> : groupes privés avec énigmes premium quotidiennes.</li>
      </ul>
      <h2>7. Règles de conduite</h2>
      <p>Il est interdit notamment de :</p>
      <ul>
        <li>Utiliser des bots, scripts ou outils automatisés ;</li>
        <li>Exploiter des bugs pour obtenir un avantage ;</li>
        <li>Harceler d&apos;autres joueurs ;</li>
        <li>Contourner les systèmes de score, Elo ou classement ;</li>
        <li>Usurper l&apos;identité d&apos;autrui ou de l&apos;éditeur.</li>
      </ul>
      <h2>8. Contenu premium</h2>
      <p>
        L&apos;accès premium est une licence d&apos;utilisation numérique sur ton compte,
        non transférable ni remboursable une fois activé, sauf obligation légale (voir <Link to="/cgv">CGV</Link>).
      </p>
      <h2>9. Propriété intellectuelle</h2>
      <p>
        Le jeu, ses énigmes, marques et contenus restent la propriété de PrograGames ou de ses concédants
        (photographes Pexels/Unsplash — voir <Link to="/credits">crédits photos</Link>).
      </p>
      <h2>10. Données personnelles</h2>
      <p>
        Le traitement des données est décrit dans la{' '}
        <Link to="/confidentialite">politique de confidentialité</Link>.
      </p>
      <h2>11. Responsabilité</h2>
      <p>
        Le jeu est fourni « en l&apos;état ». PrograGames ne garantit pas un fonctionnement ininterrompu.
      </p>
      <h2>12. Droit applicable</h2>
      <p>Les présentes CGU sont soumises au <strong>droit français</strong>.</p>
    </>
  );
}

function CGUBodyEn() {
  return (
    <>
      <h2>1. Purpose</h2>
      <p>
        These Terms of Use govern access to and use of the online game{' '}
        <strong>Dezoom</strong>, published by PrograGames (Florian Augeard). By accessing the game,
        you accept these terms.
      </p>
      <h2>2. Publisher</h2>
      <p>
        PrograGames — Florian Augeard, 49000 Angers, France. Contact:{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
        .
      </p>
      <h2>3. Access to the game</h2>
      <p>
        Dezoom is a photo guessing game in the browser. The daily challenge is free.
        Infinite mode, ranked arena and some advanced features require premium purchase
        (see <Link to="/cgv">Terms of sale</Link>). An Internet connection is required.
      </p>
      <h2>4. Age and capacity</h2>
      <p>
        The game is intended for users aged <strong>16 and over</strong>. Minors must use the service
        with parental or guardian consent.
      </p>
      <h2>5. Player account</h2>
      <ul>
        <li>You may play as a <strong>guest</strong> or create an <strong>account</strong> (email, display name).</li>
        <li>You are responsible for keeping your access credentials secure.</li>
        <li>Display names must be unique and must not infringe third-party rights.</li>
        <li>Progress (streak, scores, Elo, leagues) is tied to your logged-in account.</li>
      </ul>
      <h2>6. Features</h2>
      <ul>
        <li><strong>Daily challenge</strong>: one official puzzle per day with a daily leaderboard.</li>
        <li><strong>Infinite mode</strong> (premium): unlimited puzzles from the premium bank.</li>
        <li><strong>Arena</strong> (premium): ranked best-of-5 matches with Elo rating.</li>
        <li><strong>Friends</strong>: add by display name, challenge friends (premium).</li>
        <li><strong>Leagues</strong>: private groups with daily premium puzzles.</li>
      </ul>
      <h2>7. Code of conduct</h2>
      <p>The following is prohibited, including but not limited to:</p>
      <ul>
        <li>Using bots, scripts or automated tools ;</li>
        <li>Exploiting bugs for unfair advantage ;</li>
        <li>Harassing other players ;</li>
        <li>Circumventing score, Elo or leaderboard systems ;</li>
        <li>Impersonating others or the publisher.</li>
      </ul>
      <h2>8. Premium content</h2>
      <p>
        Premium access is a digital usage license on your account, non-transferable and non-refundable
        once activated, except where required by law (see <Link to="/cgv">Terms of sale</Link>).
      </p>
      <h2>9. Intellectual property</h2>
      <p>
        The game, puzzles, branding and content remain the property of PrograGames or its licensors
        (Pexels/Unsplash photographers — see <Link to="/credits">photo credits</Link>).
      </p>
      <h2>10. Personal data</h2>
      <p>
        Data processing is described in our{' '}
        <Link to="/confidentialite">privacy policy</Link>.
      </p>
      <h2>11. Liability</h2>
      <p>The game is provided &quot;as is&quot;. PrograGames does not guarantee uninterrupted operation.</p>
      <h2>12. Governing law</h2>
      <p>These Terms of Use are governed by <strong>French law</strong>.</p>
    </>
  );
}

function CGVBodyFr() {
  return (
    <>
      <h2>1. Vendeur</h2>
      <p>
        <strong>Florian Augeard</strong> — PrograGames<br />
        49000 Angers, France · SIREN 106 684 780 · RCS Angers<br />
        Contact :{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
      </p>
      <h2>2. Produits</h2>
      <p>Les achats portent sur du <strong>contenu numérique</strong> activable immédiatement :</p>
      <table className="legal-table">
        <thead>
          <tr><th>Produit</th><th>Description</th><th>Prix (EUR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Mode infini (à vie)</td>
            <td>Énigmes illimitées, arène Elo, galerie premium</td>
            <td>3,00 €</td>
          </tr>
        </tbody>
      </table>
      <h2>3. Commande et paiement</h2>
      <p>Paiement via <strong>Stripe</strong> (carte bancaire). Compte connecté requis.</p>
      <h2>4. Livraison</h2>
      <p>Activation <strong>immédiate</strong> après confirmation du paiement.</p>
      <h2>5. Droit de rétractation</h2>
      <p>
        Délai de <strong>14 jours</strong>, sauf renonciation expresse pour contenu numérique délivré immédiatement
        (article L221-28).
      </p>
      <h2>6. Remboursements</h2>
      <ul>
        <li>Contenu non livré : remboursement ou activation.</li>
        <li>Contenu déjà activé : pas de remboursement sauf obligation légale.</li>
      </ul>
      <h2>7. Médiation</h2>
      <p>
        Médiateur <strong>CM2C</strong> —{' '}
        <a href="https://www.cm2c.net/" target="_blank" rel="noopener noreferrer">www.cm2c.net</a>
      </p>
      <h2>8. Litiges</h2>
      <p>
        ODR :{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
      </p>
      <h2>9. CGU</h2>
      <p>L&apos;utilisation du jeu reste soumise aux <Link to="/cgu">CGU</Link>.</p>
    </>
  );
}

function CGVBodyEn() {
  return (
    <>
      <h2>1. Seller</h2>
      <p>
        <strong>Florian Augeard</strong> — PrograGames<br />
        49000 Angers, France · SIREN 106 684 780 · RCS Angers<br />
        Contact:{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
      </p>
      <h2>2. Products</h2>
      <p>Purchases cover <strong>digital content</strong> activated immediately on your account:</p>
      <table className="legal-table">
        <thead>
          <tr><th>Product</th><th>Description</th><th>Price (EUR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Infinite mode (lifetime)</td>
            <td>Unlimited puzzles, Elo arena, premium gallery</td>
            <td>€3.00</td>
          </tr>
        </tbody>
      </table>
      <h2>3. Order and payment</h2>
      <p>Payment via <strong>Stripe</strong> (card). A logged-in account is required.</p>
      <h2>4. Delivery</h2>
      <p><strong>Immediate</strong> activation after payment confirmation.</p>
      <h2>5. Right of withdrawal</h2>
      <p>
        <strong>14-day</strong> withdrawal period, except where you expressly waive it for immediately delivered digital content
        (French Consumer Code L221-28).
      </p>
      <h2>6. Refunds</h2>
      <ul>
        <li>Undelivered content: refund or activation.</li>
        <li>Already activated content: no refund except where required by law.</li>
      </ul>
      <h2>7. Mediation</h2>
      <p>
        Mediator <strong>CM2C</strong> —{' '}
        <a href="https://www.cm2c.net/" target="_blank" rel="noopener noreferrer">www.cm2c.net</a>
      </p>
      <h2>8. Disputes</h2>
      <p>
        EU ODR platform:{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
      </p>
      <h2>9. Terms of use</h2>
      <p>Use of the game remains subject to the <Link to="/cgu">Terms of use</Link>.</p>
    </>
  );
}

function MentionsBodyFr() {
  return (
    <>
      <h2>Éditeur du site et du jeu</h2>
      <p>
        <strong>Florian Augeard</strong><br />
        Entrepreneur individuel — <strong>PrograGames</strong><br />
        49000 Angers, France · SIREN 106 684 780
      </p>
      <h2>Directeur de la publication</h2>
      <p>Florian Augeard</p>
      <h2>Hébergement</h2>
      <p>
        <strong>Scaleway SAS</strong> — Paris, France —{' '}
        <a href="https://www.scaleway.com/" target="_blank" rel="noopener noreferrer">www.scaleway.com</a>
      </p>
      <h2>Propriété intellectuelle</h2>
      <p>
        Photos créditées sur <Link to="/credits">Crédits photos</Link> (Pexels, Unsplash).
      </p>
      <h2>Documents contractuels</h2>
      <ul>
        <li><Link to="/cgu">CGU</Link></li>
        <li><Link to="/cgv">CGV</Link></li>
        <li><Link to="/confidentialite">Politique de confidentialité</Link></li>
      </ul>
    </>
  );
}

function MentionsBodyEn() {
  return (
    <>
      <h2>Site and game publisher</h2>
      <p>
        <strong>Florian Augeard</strong><br />
        Sole proprietor — <strong>PrograGames</strong><br />
        49000 Angers, France · SIREN 106 684 780
      </p>
      <h2>Publication director</h2>
      <p>Florian Augeard</p>
      <h2>Hosting</h2>
      <p>
        <strong>Scaleway SAS</strong> — Paris, France —{' '}
        <a href="https://www.scaleway.com/" target="_blank" rel="noopener noreferrer">www.scaleway.com</a>
      </p>
      <h2>Intellectual property</h2>
      <p>
        Photos credited on <Link to="/credits">Photo credits</Link> (Pexels, Unsplash).
      </p>
      <h2>Legal documents</h2>
      <ul>
        <li><Link to="/cgu">Terms of use</Link></li>
        <li><Link to="/cgv">Terms of sale</Link></li>
        <li><Link to="/confidentialite">Privacy policy</Link></li>
      </ul>
    </>
  );
}

function PrivacyBodyFr() {
  return (
    <>
      <h2>1. Responsable du traitement</h2>
      <p>Florian Augeard — PrograGames, 49000 Angers, France.</p>
      <h2>2. Données collectées</h2>
      <ul>
        <li><strong>Compte</strong> : email, pseudo, avatar ;</li>
        <li><strong>Gameplay</strong> : scores, série, Elo, ligues ;</li>
        <li><strong>Social</strong> : amis, ligues ;</li>
        <li><strong>Achats</strong> : identifiants Stripe (pas de numéro de carte) ;</li>
        <li><strong>Technique</strong> : IP, logs serveur.</li>
      </ul>
      <h2>3. Finalités</h2>
      <p>Exécution du contrat, sécurité, obligations légales.</p>
      <h2>4. Sous-traitants</h2>
      <ul>
        <li><strong>Stripe</strong>, <strong>Scaleway</strong>, <strong>Mailjet</strong>.</li>
      </ul>
      <h2>5. Durées</h2>
      <p>Compte actif ; achats : 10 ans (comptabilité) ; logs : quelques mois.</p>
      <h2>6. Cookies</h2>
      <p>localStorage pour session. Pas de cookies publicitaires.</p>
      <h2>7. Tes droits (RGPD)</h2>
      <p>
        Accès, rectification, effacement — contact PrograGames. CNIL :{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">cnil.fr</a>.
      </p>
      <h2>8. Mineurs</h2>
      <p>16 ans et plus (voir <Link to="/cgu">CGU</Link>).</p>
    </>
  );
}

function PrivacyBodyEn() {
  return (
    <>
      <h2>1. Data controller</h2>
      <p>Florian Augeard — PrograGames, 49000 Angers, France.</p>
      <h2>2. Data collected</h2>
      <ul>
        <li><strong>Account</strong>: email, display name, avatar ;</li>
        <li><strong>Gameplay</strong>: scores, streak, Elo, leagues ;</li>
        <li><strong>Social</strong>: friends, leagues ;</li>
        <li><strong>Purchases</strong>: Stripe identifiers (no card numbers stored) ;</li>
        <li><strong>Technical</strong>: IP, server logs.</li>
      </ul>
      <h2>3. Purposes</h2>
      <p>Contract performance, security, legal obligations.</p>
      <h2>4. Processors</h2>
      <ul>
        <li><strong>Stripe</strong>, <strong>Scaleway</strong>, <strong>Mailjet</strong>.</li>
      </ul>
      <h2>5. Retention</h2>
      <p>Active account lifetime ; purchases: 10 years ; logs: a few months.</p>
      <h2>6. Cookies</h2>
      <p>localStorage for session. No advertising cookies.</p>
      <h2>7. Your rights (GDPR)</h2>
      <p>
        Access, rectification, erasure — contact PrograGames. CNIL:{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">cnil.fr</a>.
      </p>
      <h2>8. Minors</h2>
      <p>Aged 16 and over (see <Link to="/cgu">Terms of use</Link>).</p>
    </>
  );
}

const BODIES: Record<string, Record<Locale, () => ReactElement>> = {
  cgu: { fr: CGUBodyFr, en: CGUBodyEn },
  cgv: { fr: CGVBodyFr, en: CGVBodyEn },
  mentions: { fr: MentionsBodyFr, en: MentionsBodyEn },
  privacy: { fr: PrivacyBodyFr, en: PrivacyBodyEn },
};

export function LegalBody({ page }: { page: keyof typeof BODIES }) {
  const { locale } = useLocale();
  const Body = BODIES[page][locale];
  return <Body />;
}
